import { NEVER_DIRTY, OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { VReadOnly } from '@client/components/_shared/forms/view-form/VReadOnly';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CardTitle } from '@client/components/_ui/display/CardTitle';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { QueryState } from '@client/components/_ui/feedback/QueryState';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { createFormStore } from '@client/stores/FormStore';
import { type RouterOutputs, trpc } from '@client/trpc/client';
import { EXCESS_PAYMENT_METHODS_LABELS } from '@client/utils/financials';
import { toYesNo } from '@shared/utils/core/general';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Tag } from 'antd';
import { FileText, Receipt } from 'lucide-react';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/compensations/details/$compensationId/financials/excess-payments/details/$excessPaymentId',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Excess payment details',
        },
    },
    params: {
        parse({ excessPaymentId }) {
            return {
                excessPaymentId: BigInt(excessPaymentId),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ExcessPaymentDetails />
        </PageController>
    );
}

type ExcessPayment = NonNullable<RouterOutputs['excessPayment']['getExcessPayment']>;

const excessPaymentFormStore = createFormStore<ExcessPayment>();

function ExcessPaymentDetails() {
    const { excessPaymentId } = Route.useParams();
    const { data, isPending, error, isFetching, refetch } = trpc.excessPayment.getExcessPayment.useQuery({
        id: excessPaymentId,
    });
    const { formatCurrency } = useFormatter();
    const navigate = useNavigate();

    excessPaymentFormStore.useInit({
        data,
        isLoading: isPending,
        isReadOnly: true,
        async onReload() {
            await refetch();
        },
        async onSave() {
            return { success: true };
        },
    });

    return (
        <OverlayPage
            isDirty={NEVER_DIRTY}
            afterClose={() => navigate({ moveInMatches: -1 })}
            title="Excess payment details"
            size="middle"
            footer={({ onClose }) => <Button onClick={onClose}>Close</Button>}
        >
            <QueryState query={{ data, error, isFetching, refetch }}>
                {() => (
                    <div className="flex flex-col gap-4">
                        <Card
                            type="inner"
                            size="small"
                            title={<CardTitle icon={<Receipt />} title="Summary" />}
                            classNames={{ body: 'grid grid-cols-1 gap-x-6 gap-y-4' }}
                        >
                            <VReadOnly
                                label="Allocated amount"
                                field="amount"
                                formStore={excessPaymentFormStore}
                                formatValueFn={(value) => formatCurrency(value)}
                            />
                        </Card>

                        <Card
                            type="inner"
                            size="small"
                            title={<CardTitle icon={<FileText />} title="Details" />}
                            classNames={{ body: 'grid max-w-lg grid-cols-2 gap-x-6 gap-y-4' }}
                        >
                            <VReadOnly
                                label="Contribution method"
                                field="method"
                                formStore={excessPaymentFormStore}
                                formatValueFn={(_, item) => (
                                    <Tag variant="outlined" color="blue" className="max-w-max text-xs">
                                        {item?.method ? EXCESS_PAYMENT_METHODS_LABELS[item.method] : null}
                                    </Tag>
                                )}
                            />
                            <VReadOnly
                                label="Status"
                                field="status"
                                formStore={excessPaymentFormStore}
                                // Excess payment statuses are a subset of the excess vocabulary.
                                formatValueFn={(_, item) =>
                                    item?.status ? (
                                        <StatusBadge
                                            module="excess"
                                            status={item.status}
                                            className="max-w-max text-xs"
                                        />
                                    ) : null
                                }
                            />
                            <VReadOnly
                                label="Included in invoice"
                                field="isExcessIncludedInInvoice"
                                formStore={excessPaymentFormStore}
                                formatValueFn={(value) => toYesNo(value)}
                            />
                            <VReadOnly
                                label="Insurer approved"
                                field="isInsurerApproved"
                                formStore={excessPaymentFormStore}
                                formatValueFn={(value) => toYesNo(value)}
                            />
                        </Card>
                    </div>
                )}
            </QueryState>
        </OverlayPage>
    );
}
